import { AddIcon, DragDropIcon, OverFlowIcon } from 'assests/GmsIcons';
import BorderedButton from 'Components/Common/components/Button/BorderedButton';
import RenameIterationDialog from './RenameIterationDialog';
import React, { useState, useRef, useEffect } from 'react';

type IterationSidebarProps = {
    iterations: Iteration[];
    activeIteration: string | null;
    setActiveIteration: (id: string) => void;
    addIteration: () => void;
    removeIteration: (id: string) => void;
    reorderIterations: (sourceIndex: number, destinationIndex: number) => void;
    copyIteration: (id: string) => void;
    renameIteration: (id: string, newName: number) => void;
};

const IterationSidebar: React.FC<IterationSidebarProps> = ({
    iterations,
    activeIteration,
    setActiveIteration,
    addIteration,
    removeIteration,
    reorderIterations,
    copyIteration,
    renameIteration
}) => {
    const [draggedItemId, setDraggedItemId] = useState<string | null>(null);
    const [dragOverItemId, setDragOverItemId] = useState<string | null>(null);
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const [showRenameDialog, setShowRenameDialog] = useState<boolean>(false);
    const [iterationToRename, setIterationToRename] = useState<Iteration | null>(null);

    const menuRef = useRef<HTMLDivElement>(null);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setOpenMenuId(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleDragStart = (e: React.DragEvent<HTMLDivElement>, id: string) => {
        setDraggedItemId(id);
        if (e.dataTransfer.setDragImage) {
            const element = document.getElementById(`iteration-${id}`);
            if (element) {
                e.dataTransfer.setDragImage(element, 20, 20);
            }
        }
        e.dataTransfer.setData('text/plain', id);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>, id: string) => {
        e.preventDefault();
        if (id !== draggedItemId) {
            setDragOverItemId(id);
        }
    };

    const handleDragEnd = () => {
        setDraggedItemId(null);
        setDragOverItemId(null);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>, targetId: string) => {
        e.preventDefault();

        if (draggedItemId && draggedItemId !== targetId) {
            const sourceIndex = iterations.findIndex(item => item.id === draggedItemId);
            const targetIndex = iterations.findIndex(item => item.id === targetId);

            if (sourceIndex !== -1 && targetIndex !== -1) {
                reorderIterations(sourceIndex, targetIndex);
            }
        }

        setDraggedItemId(null);
        setDragOverItemId(null);
    };

    const handleOverflowClick = (e: React.MouseEvent, iterationId: string) => {
        e.stopPropagation();
        setOpenMenuId(openMenuId === iterationId ? null : iterationId);
    };

    const handleCopy = (iterationId: string) => {
        copyIteration(iterationId);
        setOpenMenuId(null);
    };

    const handleRenameStart = (iteration: Iteration) => {
        setIterationToRename(iteration);
        setShowRenameDialog(true);
        setOpenMenuId(null);
    };

    const handleRenameCancel = () => {
        setShowRenameDialog(false);
        setIterationToRename(null);
    };

    const handleRenameSave = (newName: number) => {
        if (iterationToRename) {
            renameIteration(iterationToRename.id, newName);
        }
        setShowRenameDialog(false);
        setIterationToRename(null);
    };

    const handleDelete = (iterationId: string) => {
        removeIteration(iterationId);
        setOpenMenuId(null);
    };

    return (
        <div className="tw-w-[15%] tw-border-r" data-testid="iteration-sidebar">
            {iterations.map((iteration) => (
                <div
                    id={`iteration-${iteration.id}`}
                    key={iteration.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, iteration.id)}
                    onDragOver={(e) => handleDragOver(e, iteration.id)}
                    onDragEnd={handleDragEnd}
                    onDrop={(e) => handleDrop(e, iteration.id)}
                    className={`tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-cursor-pointer tw-border-b tw-relative
                       ${activeIteration === iteration.id ? 'tw-bg-gray-100' : ''}
                       ${draggedItemId === iteration.id ? 'tw-opacity-50' : ''}
                       ${dragOverItemId === iteration.id ? 'tw-border-t-2 tw-border-blue-500' : ''}`}
                    onClick={() => setActiveIteration(iteration.id)}
                    data-testid={`iteration-${iteration.priority}`}
                >
                    <div className="tw-flex tw-items-center tw-gap-2 tw-flex-1">
                        <div className="tw-cursor-move">
                            <DragDropIcon />
                        </div>
                        <span className="tw-font-medium">Iteration {iteration.name}</span>
                    </div>

                    <div className="tw-relative">
                        <button
                            className="tw-text-gray-400 hover:tw-text-gray-600 tw-p-1"
                            onClick={(e) => handleOverflowClick(e, iteration.id)}
                            data-testid={`overflow-menu-${iteration.priority}`}
                        >
                            <OverFlowIcon />
                        </button>

                        {openMenuId === iteration.id && (
                            <div
                                ref={menuRef}
                                className="tw-absolute tw-right-0 tw-top-full tw-mt-1 tw-bg-white tw-rounded-[4px] tw-z-50 tw-ring-1 tw-ring-[#EBECF0]"
                                data-testid={`overflow-menu-options-${iteration.id}`}
                                role="menu"
                                aria-orientation="vertical"
                                aria-labelledby="options-menu"
                            >
                                <div className="tw-py-1 tw-border" role="menuitem">
                                    <button
                                        className="tw-block tw-px-4 tw-py-1 tw-text-sm tw-text-[#172b4d] hover:tw-bg-[#F0F3FE] tw-w-full tw-text-left"
                                        onClick={() => handleCopy(iteration.id)}
                                        data-testid={`copy-iteration-${iteration.id}`}
                                    >
                                        Copy
                                    </button>
                                </div>
                                <div className="tw-py-1 tw-border" role="menuitem">
                                    <button
                                        className="tw-block tw-px-4 tw-py-1 tw-text-sm tw-text-[#172b4d] hover:tw-bg-[#F0F3FE] tw-w-full tw-text-left"
                                        onClick={() => handleRenameStart(iteration)}
                                        data-testid={`rename-iteration-${iteration.id}`}
                                    >
                                        Rename
                                    </button>
                                </div>
                                <div className="tw-py-1 tw-border" role="menuitem">
                                    <button
                                        className="tw-block tw-px-4 tw-py-1 tw-text-sm tw-text-[#172b4d] hover:tw-bg-[#F0F3FE] tw-w-full tw-text-left"
                                        onClick={() => handleDelete(iteration.id)}
                                        data-testid={`delete-iteration-${iteration.id}`}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            ))}

            <div className="tw-p-4">
                <BorderedButton
                    onClick={addIteration}
                    className="!tw-border-primary !tw-text-primary"
                    data-testid="add-iteration-button"
                >
                    <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
                        <AddIcon color={'#091CAA'} />
                        <span>Add Iteration</span>
                    </div>
                </BorderedButton>
            </div>

            <RenameIterationDialog
                isOpen={showRenameDialog}
                currentName={iterationToRename?.name || 1}
                existingNames={iterations.map(iteration => iteration.name)}
                onSave={handleRenameSave}
                onCancel={handleRenameCancel}
            />
        </div>
    );
};

export default IterationSidebar;